import React, { useEffect, useState } from 'react';
import { listenToDocument, updateDocumentProperty } from '../../../utils/firestore-functions';
import { GetDB } from '../../../utils/database/firebase-budget-tool';
import { WhiteForm, currencyOptions, GreyLabel, PrimaryButton, Colors, WhiteTableContainer, EditIcon, HighlightedInputGroupText } from '../../../utils/budget-tool-specific';
import { Row, Col, Input, Alert, FormGroup, InputGroup } from 'reactstrap';
import FlightProviderToggle from './components/FlightProviderToggle';
import FlightExperimentalInputRow from './flight-experimental-input-row';
import { calculatePeople, calculateAvgAvgPrice, calculateAvgDuration, calculateAvgMinPrice, calculateAvgMaxPrice, calculateAvgMinDuration, calculateAvgMaxDuration, calculateAvgMinStops, calculateAvgMaxStops } from '../flights/flight-helper';

const FlightExperimentalGuideInput = (props) => {
    const budgetDB = GetDB();

    const [data, setData] = useState({
        title: {value: "", invalid: false, required: true},
        destination: {value: "", invalid: false, required: true},
        departureDate: {value: "", invalid: false, required: true},
        returnDate: {value: "", invalid: false, required: true},
        travelFee: {value: 0, invalid: false, required: true},
        currency: {value: "CAD$", invalid: false, required: true},
        flights: {value: [], invalid: false, required: true},
        flightProvider: {value: "flightapi", invalid: false, required: true},
    });

    const [flightProvider, setFlightProvider] = useState(data.flightProvider.value);

    useEffect(() => {
        if (props.id) {
            var unsubscribe = listenToDocument(budgetDB, "flightGuidesExperimental", props.id, SetGuide);
            return unsubscribe;
        }
    }, [props.id]);

    const SetGuide = (guide) => {
        if (!guide) return;

        let newData = {...data};
        Object.keys(newData).forEach(key => {
            if (guide[key] !== undefined) {
                newData[key].value = guide[key];
            }
        });
        setData(newData);

        // Update flight provider state if it was saved
        if (guide.flightProvider !== undefined) {
            setFlightProvider(guide.flightProvider);
        }
    };

    const handleInputChange = (field, value) => {
        let newData = {...data};
        newData[field].value = value;
        newData[field].invalid = false;
        setData(newData);

        // Save to database immediately
        if (props.id) {
            updateDocumentProperty(budgetDB, "flightGuidesExperimental", props.id, field, value);
        }
    };

    const handleProviderChange = (newProvider) => {
        setFlightProvider(newProvider);
        handleInputChange('flightProvider', newProvider);
    };

    const addFlight = () => {
        const newFlight = {
            id: `flight_${Date.now()}`,
            originAirport: "",
            numPeople: 1,
            priceAverage: 0,
            priceMin: 0,
            priceMax: 0,
            durationAverage: 0,
            durationMin: 0,
            durationMax: 0,
            stopsAverage: 0,
            stopsMin: 0,
            stopsMax: 0
        };

        const updatedFlights = [...data.flights.value, newFlight];
        handleInputChange('flights', updatedFlights);
    };

    const updateFlight = (index, updatedFlight) => {
        const updatedFlights = [...data.flights.value];
        updatedFlights[index] = updatedFlight;
        handleInputChange('flights', updatedFlights);
    };

    const deleteFlight = (index) => {
        const updatedFlights = data.flights.value.filter((_, i) => i !== index);
        handleInputChange('flights', updatedFlights);
    };

    const displayMinutes = (value) => {
        if (!value) return "0 min";
        let text = "";
        let minutes = value % 60;
        let hours = Math.floor(value / 60);
        if (hours !== 0) {
            text += hours + " hr ";
        }
        text += minutes + " min";
        return text;
    };

    const displayDollars = (value) => {
        if (!value) return "0";
        return value.toLocaleString("en-US");
    };

    return (
        <div style={{marginRight: "15px", marginLeft: "15px"}}>
            <WhiteTableContainer>
                <h5 style={{color: Colors.primary, marginBottom: "20px"}}>
                    🚀 Flight Guide (exp.)
                </h5>

                <WhiteForm>
                    <Row>
                        <Col lg="4">
                            <GreyLabel>Guide Title</GreyLabel>
                            <Input
                                type="text"
                                value={data.title.value}
                                onChange={(e) => handleInputChange('title', e.target.value)}
                                placeholder="Enter guide title"
                                style={{marginBottom: "15px"}}
                            />
                        </Col>
                        <Col lg="4">
                            <GreyLabel>Currency</GreyLabel>
                            <Input
                                type="select"
                                value={data.currency.value}
                                onChange={(e) => handleInputChange('currency', e.target.value)}
                                style={{marginBottom: "15px"}}
                            >
                                {currencyOptions()}
                            </Input>
                        </Col>
                        <Col lg="4">
                            <GreyLabel>Travel Fee ({data.currency.value.slice(3)})</GreyLabel>
                            <Input
                                type="number"
                                value={data.travelFee.value}
                                onChange={(e) => handleInputChange('travelFee', parseFloat(e.target.value) || 0)}
                                placeholder="0"
                                min="0"
                                step="0.01"
                                style={{marginBottom: "15px"}}
                                disabled={props.client}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col lg="4">
                            <GreyLabel>Destination (Airport Code)</GreyLabel>
                            <Input
                                type="text"
                                value={data.destination.value}
                                onChange={(e) => handleInputChange('destination', e.target.value.toUpperCase())}
                                placeholder="e.g. LAX"
                                maxLength={3}
                                style={{marginBottom: "15px"}}
                            />
                        </Col>
                        <Col lg="4">
                            <GreyLabel>Departure Date</GreyLabel>
                            <Input
                                type="date"
                                value={data.departureDate.value}
                                onChange={(e) => handleInputChange('departureDate', e.target.value)}
                                style={{marginBottom: "15px"}}
                            />
                        </Col>
                        <Col lg="4">
                            <GreyLabel>Return Date</GreyLabel>
                            <Input
                                type="date"
                                value={data.returnDate.value}
                                onChange={(e) => handleInputChange('returnDate', e.target.value)}
                                style={{marginBottom: "15px"}}
                            />
                        </Col>
                    </Row>
                </WhiteForm>

                {/* Flight Provider Toggle */}
                <FlightProviderToggle
                    provider={flightProvider}
                    onProviderChange={handleProviderChange}
                />
            </WhiteTableContainer>

            {/* Flight Rows */}
            <WhiteTableContainer>
                <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px"}}>
                    <h6 style={{color: Colors.primary, margin: "0"}}>
                        Flight Origins ({data.flights.value.length})
                    </h6>
                    <PrimaryButton
                        onClick={addFlight}
                        style={{
                            padding: "8px 16px",
                            fontSize: "14px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px"
                        }}
                    >
                        <EditIcon icon={['fas', 'plus']} style={{ height: "14px" }} />
                        Add Flight Origin
                    </PrimaryButton>
                </div>

                {data.flights.value.length === 0 ? (
                    <Alert color="info" style={{textAlign: "center"}}>
                        <strong>No flight origins added yet.</strong><br />
                        Click "Add Flight Origin" to start adding flights with different origin airports.
                    </Alert>
                ) : (
                    data.flights.value.map((flight, index) => (
                        <FlightExperimentalInputRow
                            key={flight.id}
                            flight={flight}
                            index={index}
                            data={data}
                            provider={flightProvider}
                            updateFlight={updateFlight}
                            deleteFlight={deleteFlight}
                            client={props.client}
                        />
                    ))
                )}

                {/* Flight Aggregates */}
                {data.flights.value.length > 0 && (
                    <div style={{marginTop: "30px"}}>
                        <h6 style={{color: Colors.primary, marginBottom: "15px"}}>
                            Flight Averages
                        </h6>
                        <Row>
                            <Col>
                                <FormGroup>
                                    <GreyLabel>Total People</GreyLabel>
                                    <InputGroup>
                                        <Input
                                            style={{backgroundColor: "#f7e7bc", borderBottom: "none"}}
                                            value={calculatePeople(data.flights.value)}
                                            readOnly
                                        />
                                    </InputGroup>
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup>
                                    <GreyLabel>Total Cost</GreyLabel>
                                    <InputGroup>
                                        <HighlightedInputGroupText style={{backgroundColor: "#f7e7bc", border: "none"}}>
                                            {data.currency.value.slice(3)}
                                        </HighlightedInputGroupText>
                                        <Input
                                            style={{backgroundColor: "#f7e7bc", borderBottom: "none"}}
                                            value={displayDollars(calculateAvgAvgPrice(data.flights.value))}
                                            readOnly
                                        />
                                    </InputGroup>
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup>
                                    <GreyLabel>Average Cost Per Person</GreyLabel>
                                    <InputGroup>
                                        <HighlightedInputGroupText style={{backgroundColor: "#f7e7bc", border: "none"}}>
                                            {data.currency.value.slice(3)}
                                        </HighlightedInputGroupText>
                                        <Input
                                            style={{backgroundColor: "#f7e7bc", borderBottom: "none"}}
                                            value={displayDollars(Math.trunc(calculateAvgAvgPrice(data.flights.value)/calculatePeople(data.flights.value)))}
                                            readOnly
                                        />
                                    </InputGroup>
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup>
                                    <GreyLabel>Average Duration</GreyLabel>
                                    <InputGroup>
                                        <Input
                                            style={{backgroundColor: "#f7e7bc", borderBottom: "none"}}
                                            value={displayMinutes(Math.trunc(calculateAvgDuration(data.flights.value)/calculatePeople(data.flights.value)))}
                                            readOnly
                                        />
                                    </InputGroup>
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup>
                                    <GreyLabel>Average of Min Price</GreyLabel>
                                    <InputGroup>
                                        <HighlightedInputGroupText style={{backgroundColor: "#f7e7bc", border: "none"}}>
                                            {data.currency.value.slice(3)}
                                        </HighlightedInputGroupText>
                                        <Input
                                            style={{backgroundColor: "#f7e7bc", borderBottom: "none"}}
                                            value={displayDollars(Math.trunc(calculateAvgMinPrice(data.flights.value)/calculatePeople(data.flights.value)))}
                                            readOnly
                                        />
                                    </InputGroup>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <FormGroup>
                                    <GreyLabel>Average of Max Price</GreyLabel>
                                    <InputGroup>
                                        <HighlightedInputGroupText style={{backgroundColor: "#f7e7bc", border: "none"}}>
                                            {data.currency.value.slice(3)}
                                        </HighlightedInputGroupText>
                                        <Input
                                            style={{backgroundColor: "#f7e7bc", borderBottom: "none"}}
                                            value={displayDollars(Math.trunc(calculateAvgMaxPrice(data.flights.value)/calculatePeople(data.flights.value)))}
                                            readOnly
                                        />
                                    </InputGroup>
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup>
                                    <GreyLabel>Average of Min Duration</GreyLabel>
                                    <InputGroup>
                                        <Input
                                            style={{backgroundColor: "#f7e7bc", borderBottom: "none"}}
                                            value={displayMinutes(Math.trunc(calculateAvgMinDuration(data.flights.value)/calculatePeople(data.flights.value)))}
                                            readOnly
                                        />
                                    </InputGroup>
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup>
                                    <GreyLabel>Average of Max Duration</GreyLabel>
                                    <InputGroup>
                                        <Input
                                            style={{backgroundColor: "#f7e7bc", borderBottom: "none"}}
                                            value={displayMinutes(Math.trunc(calculateAvgMaxDuration(data.flights.value)/calculatePeople(data.flights.value)))}
                                            readOnly
                                        />
                                    </InputGroup>
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup>
                                    <GreyLabel>Average of Min Stops</GreyLabel>
                                    <InputGroup>
                                        <Input
                                            style={{backgroundColor: "#f7e7bc", borderBottom: "none"}}
                                            value={(Math.trunc((calculateAvgMinStops(data.flights.value)/calculatePeople(data.flights.value))*100))/100}
                                            readOnly
                                        />
                                    </InputGroup>
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup>
                                    <GreyLabel>Average of Max Stops</GreyLabel>
                                    <InputGroup>
                                        <Input
                                            style={{backgroundColor: "#f7e7bc", borderBottom: "none"}}
                                            value={(Math.trunc((calculateAvgMaxStops(data.flights.value)/calculatePeople(data.flights.value))*100))/100}
                                            readOnly
                                        />
                                    </InputGroup>
                                </FormGroup>
                            </Col>
                        </Row>
                    </div>
                )}
            </WhiteTableContainer>

            {/* Instructions */}
            <Alert color="info" style={{marginTop: "20px"}}>
                <strong>How to use the Experimental Flight Tool:</strong>
                <ol style={{marginBottom: "0", paddingLeft: "20px"}}>
                    <li>Fill in the guide title, destination airport code, and travel dates</li>
                    <li>Choose your preferred flight data provider (FlightApi.io vs Amadeus)</li>
                    <li>Add flight origins by clicking "Add Flight Origin"</li>
                    <li>For each flight origin, enter the 3-letter origin airport code and number of passengers</li>
                    <li>Click the search button (🔄) for each flight origin to get pricing and duration data</li>
                    <li>Compare results from different providers and origins</li>
                </ol>
                <p style={{marginTop: "10px", marginBottom: "0", fontSize: "12px", color: "#6c757d"}}>
                    <strong>Note:</strong> This experimental tool allows you to test and compare data from different flight providers. Each flight origin can be searched independently.
                </p>
            </Alert>
        </div>
    );
};

export default FlightExperimentalGuideInput;